import './Game.styles.scss';

import { observer } from 'mobx-react';
import React, {
  FC,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { GameContext } from '../../context/GameContext/GameContext';
import useScreenDimensions from '../../hooks/useScreenDimensions';
import { useWebsocketSubscription } from '../../hooks/useWebsocketSubscription';
import { Card } from '../../model/Card';
import { GameStage } from '../../model/enumeration/GameStage.enum';
import { Permission } from '../../model/enumeration/Permission.enum';
import { Player as PlayerModel } from '../../model/Player';
import authStore from '../../store/AuthStore';
import commonStore from '../../store/CommonStore';
import gameStore from '../../store/GameStore';
import permissionStore from '../../store/PermissionStore';
import playerStore from '../../store/PlayerStore';
import { GameState } from '../../store/state/GameState';
import { GameTable } from '../../store/state/GameTable';
import tableStore from '../../store/TableStore';
import userSettingsStore from '../../store/UserSettingsStore';
import { getHolesLength, getTableImage } from '../../utils/game-types.utils';
import logger from '../../utils/logger.utils';
import { WEventsDispatcher } from '../../websocket/handlers/WEventsDispatcher';
import { CardModel } from '../Card/Card.types';
import MultiCard from '../Card/MultiCard/MultiCard';
import GameControlButtons from '../GameControlButtons/GameControlButtons';
import EmptyPlayer from '../Player/EmptyPlayer/EmptyPlayer';
import Player from '../Player/Player';
import PlayerBet from '../PlayerBet/PlayerBet';
import AnimatedTargetEmojiDrawer from '../TargetEmoji/AnimatedTargetEmojiDrawer/AnimatedTargetEmojiDrawer';
import { GameProps, PlayerPosition } from './Game.props';
import GameId from './GameInfo/GameId/GameId';
import Pot from './Pot/Pot';
import { calculatePlayerPositions } from './table.render.utils';

const Game: FC<GameProps> = ({
  tableId,
  isolated,
  isModalWindow,
  onGameControlButtonsChange,
}) => {
  const [playerPositions, setPlayerPositions] = useState<PlayerPosition[]>([]);

  const [game, setGame] = useState<GameState>();
  const [table, setTable] = useState<GameTable>();

  const tableRef = useRef<HTMLDivElement>(null);

  const isChatAndBotAiErrorsInitialized = useRef<boolean>(false);

  const { isSingleTable } = useContext(GameContext);

  const { screenWidth, screenHeight } = useScreenDimensions();

  const tableUnion = useMemo(
    () => (table ? tableStore.getUnion(tableId) : {}),
    [table, tableId, table?.players?.length],
  );

  const playingPlayersSize = useMemo(
    () => playerPositions.filter(p => p.player).length,
    [playerPositions, playerPositions.length],
  );

  const tableCards = useMemo(
    () =>
      game?.board
        ? game.board.map(
            card =>
              ({
                ...card,
                reversed: false,
              } as CardModel),
          )
        : [],
    [game, game?.board],
  );

  const manualBotMoveControlEnabled = useMemo(
    () =>
      permissionStore.hasPermission(Permission.MANUAL_BOT_MOVE_CONTROL) &&
      userSettingsStore.settings.table.manualBotMoveControl,
    [
      authStore.authenticatedUser,
      userSettingsStore.settings.table.manualBotMoveControl,
    ],
  );

  const isVerticalGameControlButtons = useMemo(
    () =>
      ((isSingleTable && !commonStore.getIsSidebarPinned()) || isModalWindow) &&
      screenWidth >= 1920 &&
      !commonStore.isExpertModeEnabled,
    [
      isSingleTable,
      isModalWindow,
      screenWidth,
      commonStore.isExpertModeEnabled,
      commonStore.isSidebarPinned,
    ],
  );

  const compareCards = (card1: Card, card2: Card): boolean => {
    return (
      card1?.cardNumber === card2?.cardNumber && card1?.suit === card2?.suit
    );
  };

  const getTableCards = useCallback(
    (board: Card[]): CardModel[] => {
      const cards = board.map((card, index) => {
        if (game?.board[index] && compareCards(card, game?.board[index])) {
          return null;
        } else
          return {
            ...card,
            reversed: false,
          } as CardModel;
      });
      if (cards.length < 5 && cards.length !== game?.board?.length) {
        return new Array(5 - cards.length).fill(null).concat(cards);
      }
      return cards;
    },
    [game?.board, game?.board?.length, game, game?.board3, game?.board2],
  );

  const playerCardsLength = useMemo(
    () => (table ? getHolesLength(table.currentGameType) : 0),
    [table?.currentGameType],
  );

  const loadGameData = useCallback(() => {
    Promise.all([
      tableStore.findActiveTableById(tableId),
      gameStore.findActiveGame(tableId),
    ]).then(response => {
      setTable(response[0]);
      setGame(response[1]);
    });
  }, [tableId]);

  useEffect(() => {
    isChatAndBotAiErrorsInitialized.current = false;
    loadGameData();
  }, [tableId]);

  useEffect(() => {
    if (gameStore.reloadGame) {
      loadGameData();
    }
  }, [gameStore.reloadGame]);

  useEffect(() => {
    gameStore.findActiveGame(tableId).then(response => setGame(response));
  }, [table?.gameId]);

  useEffect(() => {
    if (table && !isolated && !table.deleted) {
      const isChatDisabled = userSettingsStore.settings.chat.disabled;
      tableStore
        .loadTableAggregatedData(
          tableId,
          !isChatDisabled,
          !isChatAndBotAiErrorsInitialized.current,
        )
        .then(() => (isChatAndBotAiErrorsInitialized.current = true));
    }
  }, [
    isolated,
    table,
    table?.deleted,
    userSettingsStore.settings.chat.disabled,
  ]);

  const onLoad = (): void => {
    if (game?.players) {
      const tableElement = tableRef.current;
      if (tableElement) {
        setPlayerPositions(
          calculatePlayerPositions(
            tableElement,
            game.players,
            isSingleTable,
            game.table.room,
          ),
        );
      }
    }
  };

  useEffect(() => {
    if (game?.players && !commonStore.isLoading) {
      onLoad();
    }
  }, [
    game,
    game?.players,
    screenWidth,
    screenHeight,
    commonStore.isLoading,
    commonStore.isSidebarPinned,
  ]);

  useEffect(() => {
    if (
      game &&
      onGameControlButtonsChange &&
      manualBotMoveControlEnabled &&
      !isolated
    ) {
      onGameControlButtonsChange({
        tableId,
        isVertical: isVerticalGameControlButtons,
        isVisible: !!(game.availableAction || game.lastAvailableAction),
      });
    }
  }, [
    game,
    isolated,
    game?.availableAction,
    game?.lastAvailableAction,
    onGameControlButtonsChange,
    isVerticalGameControlButtons,
    manualBotMoveControlEnabled,
  ]);

  const isDealer = useCallback(
    (player: PlayerModel) => table && player.pid === table.dealerPid,
    [table?.dealerPid],
  );

  const getWon = useCallback(
    (pid: string): number => {
      if (game?.players) {
        const player = playerStore.findPlayer(pid, game.players);
        return player ? player.won : 0;
      }
    },
    [game, game?.players, game?.stage],
  );

  const getLost = useCallback(
    (pid: string): number => {
      if (game?.players) {
        const player = playerStore.findPlayer(pid, game.players);
        return player ? player.lost : 0;
      }
    },
    [game, game?.players, game?.stage],
  );

  const isLast = useCallback(
    (pid: string): boolean => {
      if (game?.players) {
        const leftPlayers = game.players.filter(
          player => player && !player.folded,
        );
        if (leftPlayers.length === 1) {
          return leftPlayers[0].pid === pid;
        }
        return false;
      }
    },
    [game, game?.players],
  );

  useWebsocketSubscription(
    `/user/tables/${tableId}/game-events`,
    WEventsDispatcher.processEventMessage,
    tableId,
  );

  const getGlowingCardsIndexes = useCallback(
    (cards: Card[]) => {
      if (game?.board && cards) {
        const glowingCardsIndexes: number[] = [];
        for (let i = 0; i < game?.board.length; i++) {
          if (compareCards(game?.board[i], cards[i])) {
            glowingCardsIndexes.push(i);
          }
        }
        return glowingCardsIndexes;
      }
    },
    [game?.shownWinBoard],
  );

  return game && table ? (
    <>
      <div className="Game-Component">
        <div
          ref={tableRef}
          id={`table-${tableId}`}
          className="game p-2 position-relative"
          onClick={() => logger.log(game)}
        >
          {playerPositions.map(
            ({ player, tablePosition, betPosition }, index) =>
              player ? (
                <Fragment key={player.pid}>
                  <Player
                    table={table}
                    player={player}
                    tablePosition={tablePosition}
                    isDealer={isDealer(player)}
                    isActive={player.pid === game.activePid}
                    isPlayerGlowing={player.pid === table.glowingPid}
                    gameStage={game.stage}
                    won={getWon(player.pid)}
                    loss={getLost(player.pid)}
                    stepDuration={table.playerStepDuration}
                    isLast={isLast(player.pid)}
                    cardsLength={playerCardsLength}
                    tableBB={table.bb}
                    turnStartingTime={game.turnStartingTime}
                    isolated={isolated}
                  />
                  <PlayerBet
                    totalBet={commonStore.convertMoney(
                      player.totalBet,
                      table.bb,
                    )}
                    playerBetPosition={betPosition}
                  />
                </Fragment>
              ) : (
                <EmptyPlayer
                  key={`${tablePosition.playerX}-${tablePosition.playerY}`}
                  tableId={tableId}
                  room={table.room}
                  gameType={table.gameType}
                  limit={table.limit}
                  tableUnion={tableUnion}
                  place={index}
                  tablePlayerCount={table.players.length}
                  isolated={isolated || playingPlayersSize === 0}
                  tablePosition={tablePosition}
                />
              ),
          )}
          <AnimatedTargetEmojiDrawer
            tableId={tableId}
            playerPositions={playerPositions}
          />
          {game.board3 && (
            <MultiCard
              cards={getTableCards(game?.board3)}
              cardsPosition={{ cardX: '50%', cardY: '26%' }}
              isTableCard
              glowingCardsIndexes={
                (game.shownWinBoard === 3 &&
                  getGlowingCardsIndexes(game.board)) ||
                []
              }
            />
          )}
          {game.board2 && (
            <MultiCard
              cards={getTableCards(game?.board2)}
              cardsPosition={{ cardX: '50%', cardY: '34%' }}
              isTableCard
              glowingCardsIndexes={
                (game.shownWinBoard === 2 &&
                  getGlowingCardsIndexes(game.board)) ||
                []
              }
            />
          )}
          <MultiCard
            cards={tableCards}
            cardsPosition={{ cardX: '50%', cardY: '42%' }}
            isTableCard
            glowingCardsIndexes={
              game.stage === GameStage.GAME_FINISHED && game.shownWinBoard === 1
                ? game?.board?.map((_, index) => index)
                : getGlowingCardsIndexes(game.board2)
            }
          />
          <Pot value={commonStore.convertMoney(game.pot, table.bb)} />
          {game?.id && <GameId gameId={game.id} />}
          <img
            className="table-img w-100 h-100"
            onLoad={onLoad}
            src={getTableImage(table.currentGameType)}
          />
        </div>
      </div>
      {!isolated &&
        game?.id &&
        manualBotMoveControlEnabled &&
        (game.availableAction || game.lastAvailableAction) && (
          <GameControlButtons
            key={table.id}
            gameId={game.id}
            tableId={tableId}
            room={table.room}
            tableBB={table.bb}
            action={game.availableAction || game.lastAvailableAction}
            disabled={
              game.availableAction === null && game.lastAvailableAction !== null
            }
            isVertical={isVerticalGameControlButtons}
            isModalWindow={isModalWindow}
            onSuccessfulSendAction={() => game.setAvailableAction(null)}
          />
        )}
    </>
  ) : (
    <></>
  );
};

export default observer(Game);
